import "./MatchStandings.css";

import dayjs from "dayjs";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";

import { IStandingGroupTableColumn, SoccerMatchStatus } from "../../../../types";
import { appTournamentConfig, isNationsLeague, TOURNAMENT_ID } from "../../config/tournament";
import { useMatchStanding } from "../../hooks";
import { useScheduleMatches } from "../../hooks/useScheduleMatches";
import { CustomSelect } from "../CustomSelect";
import { DataLoader } from "../DataLoader";
import { ErrorScreen } from "../ErrorScreen";
import { StandingGroupTable } from "../StandingGroupTable";
import { useTranslation } from "react-i18next";
import { SbsAnalyticsService } from "../../utils";

export default function MatchStandings() {
  const { t } = useTranslation();
  const { data, error, isLoading, refetch } = useMatchStanding();

  const [selectedLeagueId, setSelectedLeagueId] = useState("");

  // Contestants whose match is in play right now - drives the LIVE marker in
  // each group table. Pulled from the shared schedule cache (polls live).
  const { matches } = useScheduleMatches();
  const liveTeamIds = useMemo(() => {
    const ids = new Set<string>();
    for (const m of matches) {
      if (m.status !== SoccerMatchStatus.PLAYING) continue;
      for (const c of m.contestants ?? []) {
        if (c.id) ids.add(c.id);
      }
    }
    return ids;
  }, [matches]);

  const isClubTournament = appTournamentConfig[TOURNAMENT_ID].isClubTournament;

  useEffect(() => {
    if (data && !selectedLeagueId.length) {
      const firstLeagueId = data.standings.stages[0].id;
      setSelectedLeagueId(firstLeagueId);
    }
  }, [data]);

  const leagueListOptions =
    data?.standings.stages.map((stage) => ({
      label: stage.name,
      value: stage.id,
    })) ?? [];

  const leagueListSortedOptions = [...leagueListOptions].sort((a, b) =>
    a.label.localeCompare(b.label)
  );

  const translatedLeagueListSortedOptions = leagueListSortedOptions.map(
    (option) => {
      const leagueName = option.label.split(" ");

      return {
        label: t("standingsTab.leagueName", {
          leagueNameIdentifier: leagueName[1],
        }),
        value: option.value,
      };
    }
  );

  const allRanks = data?.standings?.stages.find(
    (stage) => stage.id === selectedLeagueId,
  )?.ranks;

  // The "3rd Place Ranking" pseudo-group is the WC 2026 cross-group leaderboard
  // for 3rd-place finishers — top 8 of those 12 teams qualify for the Round of
  // 32. We hide that pseudo-group from the displayed list and instead use it to
  // mark each group's rank-3 team as qualified when applicable.
  const isThirdPlaceRanking = (name?: string) => {
    if (!name) return false;
    const n = name.toLowerCase();
    return n === "group place" || n.includes("3rd place");
  };

  const qualifiedThirdPlaceIds = useMemo(() => {
    const tpr = allRanks?.find((g) => isThirdPlaceRanking(g.groupName));
    if (!tpr) return new Set<string>();
    return new Set(
      tpr.teamRanks
        .filter((t) => t.rankStatus && t.rank <= 8)
        .map((t) => t.contestantId)
        .filter(Boolean),
    );
  }, [allRanks]);

  const groupStageList = allRanks?.filter(
    (group) =>
      (group.groupId || isClubTournament) &&
      !isThirdPlaceRanking(group.groupName),
  );

  const groupStageEndDateStr = data?.standings?.stages.find(
    (stage) => stage.id === selectedLeagueId
  )?.endDate;

  const groupStageEndDate = useMemo(
    () => dayjs(groupStageEndDateStr).hour(23).minute(59).second(59),
    [groupStageEndDateStr]
  );

  const isGroupStageEnded = dayjs().isAfter(groupStageEndDate);

  if (isLoading) {
    return (
      <DataLoader
        customStyle={{
          height: "90dvh",
        }}
      />
    );
  }

  if (error) {
    return (
      <div className="standing-error">
        <ErrorScreen refetch={refetch} />
      </div>
    );
  }

  return (
    <div className="standings-container">
      {isNationsLeague && (
        <CustomSelect
          options={translatedLeagueListSortedOptions}
          placeholder={"Select League"}
          value={selectedLeagueId}
          onChange={(value) => {
            if (value && typeof value === "string") {
              SbsAnalyticsService.trackEvent("calendar_view_change", {
                match_id: "", // standings page not tied to a specific match
                action_type: "select_from_dropdown",
              });
              setSelectedLeagueId(value);
            }
          }}
        />
      )}
      {groupStageList?.map((group, idx) => (
        <StandingGroupTable
          key={(group.groupId || "") + idx}
          data={group}
          columnsList={GroupTableColumns}
          isGroupStageEnded={isGroupStageEnded}
          qualifiedThirdPlaceIds={qualifiedThirdPlaceIds}
          liveTeamIds={liveTeamIds}
        />
      ))}
    </div>
  );
}

const GroupTableColumns: IStandingGroupTableColumn[] = [
  {
    headerLabel: t("standingsTab.standingTableColummHeader.rank"),
    propKey: "rank",
    headerClassName: "text-align-left",
    valueClassName: "team-rank-and-name",
  },
  {
    headerLabel: t("standingsTab.standingTableColummHeader.teamName"),
    propKey: "contestantName",
    headerClassName: "text-align-left",
    valueClassName: "team-rank-and-name standing-table-team-name",
  },
  {
    headerLabel: t("standingsTab.standingTableColummHeader.matchesPlayed"),
    propKey: "matchesPlayed",
    headerClassName: "",
    valueClassName: "",
  },
  {
    headerLabel: t("standingsTab.standingTableColummHeader.matchesWon"),
    propKey: "matchesWon",
    headerClassName: "",
    valueClassName: "",
  },
  {
    headerLabel: t("standingsTab.standingTableColummHeader.matchesDrawn"),
    propKey: "matchesDrawn",
    headerClassName: "",
    valueClassName: "",
  },
  {
    headerLabel: t("standingsTab.standingTableColummHeader.matchesLost"),
    propKey: "matchesLost",
    headerClassName: "",
    valueClassName: "",
  },
  {
    headerLabel: t("standingsTab.standingTableColummHeader.goalsFor"),
    propKey: "goalsFor",
    headerClassName: "",
    valueClassName: "",
  },
  {
    headerLabel: t("standingsTab.standingTableColummHeader.goalsAgainst"),
    propKey: "goalsAgainst",
    headerClassName: "",
    valueClassName: "",
  },
  {
    headerLabel: t("standingsTab.standingTableColummHeader.goalDifference"),
    propKey: "goaldifference",
    headerClassName: "",
    valueClassName: "",
  },
  {
    headerLabel: t("standingsTab.standingTableColummHeader.points"),
    propKey: "points",
    headerClassName: "pts-column",
    valueClassName: "pts-column",
  },
];
